import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

import type { LocationData } from '@/types/api/location';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';

import MapTooltip from './MapTooltip';
import topo from './topo.json';

const fillColors = {
  high: '#5a97e3',
  medium: '#a9caf1',
  low: '#e3e4e7',
  highlighted: '#1f65c1',
};

type MapGeography = {
  id: string;
  properties: {
    name: string;
  };
  rsmKey: string;
};

interface LocationMapProps {
  platformData: LocationData[];
  highlightedCountryCode?: string | null;
  roundValues?: boolean;
  valueFormatter?: (value: number) => string;
  valueScale?: 'relative' | 'percentage';
  onCountryHover?: (geography: MapGeography, plays: number) => void;
}

const formatMapValue = (value: number): string => formatBigNumbers(value);

const getPercentageFillColor = (value: number): string => {
  if (value > 20) {
    return fillColors.high;
  }

  if (value > 0) {
    return fillColors.medium;
  }

  return fillColors.low;
};

const LocationMap = ({
  platformData,
  highlightedCountryCode,
  roundValues = true,
  valueFormatter = formatMapValue,
  valueScale = 'relative',
  onCountryHover,
}: LocationMapProps) => {
  const [selectedGeography, setSelectedGeography] = useState<MapGeography | null>(null);

  const geographyValueMap = useMemo(() => {
    if (!platformData.length) {
      return new Map<string, number>();
    }

    return new Map(
      platformData.map((row: LocationData) => [
        row.label,
        typeof row.value === 'number' ? row.value : 0,
      ])
    );
  }, [platformData]);

  const getGeographyValue = useCallback(
    (id: string): number => {
      const value = geographyValueMap.get(id);
      if (typeof value !== 'number') {
        return 0;
      }

      return roundValues && valueScale !== 'percentage' ? Math.round(value) : value;
    },
    [geographyValueMap, roundValues, valueScale]
  );

  const handleMouseEnter = (geography: MapGeography) => {
    const plays = getGeographyValue(geography.id);

    setSelectedGeography(geography);
    onCountryHover?.(geography, plays);
  };

  const handleMouseLeave = () => {
    setSelectedGeography(null);
  };

  const mediumValue =
    platformData.reduce((total: number, row: LocationData) => total + (row.value ?? 0), 0) /
      platformData.length || 0;

  const getFillColor = (id: string) => {
    if (highlightedCountryCode === id) {
      return fillColors.highlighted;
    }

    if (!platformData.length) {
      return undefined;
    }

    const value = getGeographyValue(id);

    if (valueScale === 'percentage') {
      return getPercentageFillColor(value);
    }

    if (value >= mediumValue) {
      return fillColors.high;
    }
    if (value > 0) {
      return fillColors.medium;
    }
    return fillColors.low;
  };

  return (
    <>
      <ComposableMap className="max-h-[100%] h-[300]">
        <Geographies geography={topo}>
          {({ geographies }: { geographies: MapGeography[] }) =>
            geographies.map((geo) => (
              <Geography
                fill={getFillColor(geo.id)}
                className="hover:opacity-75 transition-opacity duration-100"
                stroke={'#FFFFFF'}
                key={geo.rsmKey}
                geography={geo}
                data-testid={`location-map-country-${geo.id}`}
                aria-label={geo.properties.name}
                onMouseEnter={() => {
                  handleMouseEnter(geo);
                }}
                onMouseLeave={() => {
                  handleMouseLeave();
                }}
              />
            ))
          }
        </Geographies>
      </ComposableMap>
      {selectedGeography !== null && (
        <ToolipPortal>
          <MapTooltip
            title={`${selectedGeography.properties.name} (${valueFormatter(getGeographyValue(selectedGeography.id))})`}
            offset={{ x: 10, y: 10 }}
          />
        </ToolipPortal>
      )}
    </>
  );
};

const ToolipPortal = ({ children }: { children: React.ReactNode }) => {
  return createPortal(children, document.body);
};

export default LocationMap;

